/**
 * Device identity for gateway authentication.
 *
 * Uses Ed25519 via @noble/ed25519, matching the OpenClaw Control UI implementation.
 * Keys are stored in localStorage as base64url-encoded strings.
 */

import { getPublicKeyAsync, signAsync, utils } from '@noble/ed25519';

const STORAGE_KEY = 'clawlet-device-identity-v1';

type StoredIdentity = {
  version: 1;
  deviceId: string;
  publicKey: string;
  privateKey: string;
  createdAtMs: number;
};

export interface DeviceIdentity {
  deviceId: string;
  publicKey: string; // base64url-encoded raw Ed25519 public key
  privateKey: string; // base64url-encoded raw Ed25519 private key
}

export interface DeviceConnectParams {
  id: string;
  publicKey: string;
  signature: string;
  signedAt: number;
  nonce: string | undefined;
}

// ---------------------------------------------------------------------------
// Encoding helpers
// ---------------------------------------------------------------------------

function base64UrlEncode(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) {
    binary += String.fromCharCode(byte);
  }
  return btoa(binary).replaceAll('+', '-').replaceAll('/', '_').replace(/=+$/g, '');
}

function base64UrlDecode(input: string): Uint8Array {
  const normalized = input.replaceAll('-', '+').replaceAll('_', '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4);
  const binary = atob(padded);
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

async function fingerprintPublicKey(publicKey: Uint8Array): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', publicKey.slice().buffer);
  return bytesToHex(new Uint8Array(hash));
}

// ---------------------------------------------------------------------------
// Auth payload (must match gateway's buildDeviceAuthPayload)
// ---------------------------------------------------------------------------

interface DeviceAuthPayloadParams {
  deviceId: string;
  clientId: string;
  clientMode: string;
  role: string;
  scopes: string[];
  signedAtMs: number;
  token?: string | null;
  nonce?: string | null;
}

function buildDeviceAuthPayload(params: DeviceAuthPayloadParams): string {
  const version = params.nonce ? 'v2' : 'v1';
  const scopes = params.scopes.join(',');
  const token = params.token ?? '';
  const base = [
    version,
    params.deviceId,
    params.clientId,
    params.clientMode,
    params.role,
    scopes,
    String(params.signedAtMs),
    token,
  ];
  if (version === 'v2') {
    base.push(params.nonce ?? '');
  }
  return base.join('|');
}

// ---------------------------------------------------------------------------
// Key generation and storage
// ---------------------------------------------------------------------------

async function generateIdentity(): Promise<DeviceIdentity> {
  const privateKey = utils.randomSecretKey();
  const publicKey = await getPublicKeyAsync(privateKey);
  const deviceId = await fingerprintPublicKey(publicKey);
  return {
    deviceId,
    publicKey: base64UrlEncode(publicKey),
    privateKey: base64UrlEncode(privateKey),
  };
}

export async function getOrCreateDevice(): Promise<DeviceIdentity> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as StoredIdentity;
      if (
        parsed?.version === 1 &&
        typeof parsed.deviceId === 'string' &&
        typeof parsed.publicKey === 'string' &&
        typeof parsed.privateKey === 'string'
      ) {
        // Recompute device ID from public key to ensure consistency
        const derivedId = await fingerprintPublicKey(base64UrlDecode(parsed.publicKey));
        if (derivedId !== parsed.deviceId) {
          const updated: StoredIdentity = { ...parsed, deviceId: derivedId };
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
          return {
            deviceId: derivedId,
            publicKey: parsed.publicKey,
            privateKey: parsed.privateKey,
          };
        }
        return {
          deviceId: parsed.deviceId,
          publicKey: parsed.publicKey,
          privateKey: parsed.privateKey,
        };
      }
    }
  } catch {
    // fall through to regenerate
  }

  const identity = await generateIdentity();
  const stored: StoredIdentity = {
    version: 1,
    deviceId: identity.deviceId,
    publicKey: identity.publicKey,
    privateKey: identity.privateKey,
    createdAtMs: Date.now(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  return identity;
}

// ---------------------------------------------------------------------------
// Challenge signing
// ---------------------------------------------------------------------------

async function signDevicePayload(privateKeyBase64Url: string, payload: string): Promise<string> {
  const key = base64UrlDecode(privateKeyBase64Url);
  const data = new TextEncoder().encode(payload);
  const sig = await signAsync(data, key);
  return base64UrlEncode(sig);
}

/**
 * Sign a gateway challenge and build the full `device` connect params.
 */
export async function signChallenge(
  device: DeviceIdentity,
  nonce: string | null,
  opts: {
    clientId: string;
    clientMode: string;
    role: string;
    scopes: string[];
    token?: string | null;
  },
): Promise<DeviceConnectParams> {
  const signedAtMs = Date.now();
  const payload = buildDeviceAuthPayload({
    deviceId: device.deviceId,
    clientId: opts.clientId,
    clientMode: opts.clientMode,
    role: opts.role,
    scopes: opts.scopes,
    signedAtMs,
    token: opts.token ?? null,
    nonce: nonce ?? null,
  });

  const signature = await signDevicePayload(device.privateKey, payload);

  return {
    id: device.deviceId,
    publicKey: device.publicKey,
    signature,
    signedAt: signedAtMs,
    nonce: nonce ?? undefined,
  };
}
